import {DIFF_HIGHLIGHT, extractLineHighlights, type HighlightPart} from '../../diff_utils.ts';
import {isNone, sameValueZero} from '../../js_utils.ts';
import {pagesEqual} from '../../page.ts';
import {isUrl} from '../../utils/url.ts';
import {extractSvgGroup} from '../svg_utils.ts';
import {svgApplyFilter} from '../AllSpiceDiff/AllSpiceDiffViewStyle.ts';

const XMLNS = 'http://www.w3.org/2000/svg';

type Path = Array<string>;

/**
 * A symbol ID.  It can be initialized with null to represent the sentinal value
 * of no-symbol.
 */
class SymbolId {
  id: number | null;

  constructor(id: number | null = null) {
    this.id = id;
  }

  /**
   * Returns true when this is the sentinal value representing no symbol.
   */
  isNone(): boolean {
    return isNone(this.id);
  }

  isPresent(): boolean {
    return !this.isNone();
  }

  toString(): string {
    return `${this.id}`;
  }
}

type OutputSymbol = {
  id: SymbolId,
  docId: string,
  svgText: string
};

export type RawComponent = {
  id: string;
  name: string;
  symbols_map?: Record<string, {id: number}>; // Schematic components will have this, but PCB components won't.
  ui_group_name?: string;
};

export type ComponentLibraryFileType = 'schematic' | 'library';

/**
 * The symbol ID used as a placeholder for rendering when there are only
 * elements in all symbols.
 */
const PLACEHOLDER_SYMBOL_ID = 1;

/**
 * Convenience function to create a document ID with optional symbol suffix.
 */
function createDocId(id: string, symbolId: SymbolId): string {
  return symbolId.isPresent() ?
    `${id}-${symbolId}` :
    `${id}`;
}

function symbolIdsFromRawComponent(rawComponent: RawComponent | undefined): Array<number | null> {
  if (!rawComponent) {
    // Component is not present.  This may be the case in a diff.
    return [];
  }
  if (!rawComponent.symbols_map) {
    // No symbols.  Probably a PCB component.  Use null ID to refer to the
    // component without a symbol.
    return [null];
  }

  const symbolIds = [];
  for (const key of Object.keys(rawComponent.symbols_map ?? {})) {
    /** @type {RawComponentSymbol} */
    const symbol = rawComponent.symbols_map[key];
    symbolIds.push(symbol.id);
  }

  if (symbolIds.length === 0) {
    // No symbols.  We need to render at least one for when there are only
    // elements in all symbols.
    symbolIds.push(PLACEHOLDER_SYMBOL_ID);
  }
  return symbolIds;
}

export class LibraryComponent {
  isDiff: boolean;
  isAdded: boolean;
  isRemoved: boolean;
  isModified: boolean;
  id: string;
  head: RawComponent | undefined;
  base: RawComponent | undefined;
  outputSvg: string; // SVG for the component in the summary row as a string.
  outputSymbols: OutputSymbol[];

  constructor(
    isDiff: boolean,
    rawHeadComponent: RawComponent | undefined,
    rawBaseComponent: RawComponent | undefined,
    isAdded: boolean,
    isRemoved: boolean,
    headSvg: SVGSVGElement | null,
    diffToSvg: SVGSVGElement | null,
    diffFromSvg: SVGSVGElement | null,
  ) {
    this.isDiff = isDiff;
    this.isAdded = isAdded;
    this.isRemoved = isRemoved;
    this.isModified = (!isAdded && !isRemoved && isDiff && !this.rawComponentEquals(rawHeadComponent, rawBaseComponent));

    if (!rawHeadComponent && !rawBaseComponent) {
      throw new Error('Either base or head should be present');
    }

    // Safety: Cast works because we throw if both are not present.
    this.id = (rawHeadComponent?.id ?? rawBaseComponent?.id);

    this.head = rawHeadComponent;
    this.base = rawBaseComponent;

    /**
     * SVG for the component in the summary row as a string.
     * @type {string}
     */
    this.outputSvg = '';
    /**
     * SVG for each symbol of the component as a string.
     * @type {Array<{id: SymbolId, docId: docId, svgText: string}>}
     */
    this.outputSymbols = [];
    // Ensure that output is initialized if the SVGs loaded before this.
    this.updateOutputSvg(headSvg, diffFromSvg, diffToSvg);
  }

  /**
   * CSS class names.
   */
  get classNames(): string {
    const names = ['component'];
    if (this.isDiff) {
      if (this.isAdded) {
        names.push('added');
      }
      if (this.isRemoved) {
        names.push('removed');
      }
    }
    return names.join(' ');
  }

  /**
   * Compare two components for equality
   */
  rawComponentEquals(c1: object | number | string | null | undefined, c2: object | number | string | null | undefined): boolean {
    return pagesEqual(c1, c2);
  }

  getPath(obj: RawComponent | undefined, path: Path): unknown {
    // eslint-disable-next-line unicorn/no-array-reduce
    return path.reduce<unknown>((o, k) => (o as Record<string, unknown>)?.[k], obj);
  }

  /**
   * Get the keys for some object within the component (duplicates are removed)
   */
  allChildKeys(startPath: Path): Array<string> {
    const base = this.getPath(this.base, startPath);
    const head = this.getPath(this.head, startPath);
    let baseKeys: string[] = [];
    if (base !== undefined && typeof base === 'object' && base !== null) {
      baseKeys = Object.keys(base);
    }
    if (head !== undefined && typeof head === 'object' && head !== null) {
      return Array.from(new Set([...Object.keys(head), ...baseKeys]));
    }
    return baseKeys;
  }

  /**
   * Check if the head or base value by the given path should be formatted
   * as a hyperlink
   * @param {RawComponent | undefined} diffObj
   * @param {Path} path
   * @returns {boolean}
   */
  shouldCreateHyperlink(diffObj: RawComponent | undefined, path: Path) {
    const val = this.getPath(diffObj, path);
    if (isNone(val)) {
      return false;
    }
    return isUrl(val.toString());
  }

  /**
   * Returns true if the property exists in the head or base of the component
   * @param {Path} path
   * @returns {boolean}
   */
  hasProperty(path: Path) {
    return this.getPath(this.base, path) !== undefined ||
      this.getPath(this.head, path) !== undefined;
  }

  /**
   * Returns true if the component should appear in the diff
   * @returns {boolean}
   */
  hasChanged() {
    return this.isDiff && (this.isAdded || this.isRemoved || this.isModified);
  }

  /**
   * Returns true if we're doing a diff and the given property changed between
   * base and head.
   * @param {Path} path
   * @returns {boolean}
   */
  isPropertyChanged(path: Path) {
    return this.isDiff && (this.isAdded || this.isRemoved ||
      !sameValueZero(this.getPath(this.base, path),
        this.getPath(this.head, path)));
  }

  /**
   * @param {Path} path
   * @returns {LineHighlights}
   */
  extractLineHighlights(path: Path) {
    const baseObj = this.getPath(this.base, path);
    const headObj = this.getPath(this.head, path);
    const base = isNone(baseObj) ? baseObj : baseObj.toString();
    const head = isNone(headObj) ? headObj : headObj.toString();
    return extractLineHighlights(base, head);
  }

  /**
   * @param {HighlightPart} part
   * @returns {boolean}
   */
  shouldHighlight(part: HighlightPart) {
    return part.kind === DIFF_HIGHLIGHT;
  }

  /**
   * @param {SVGSVGElement | null} headSvg
   * @param {SVGSVGElement | null} diffFromSvg
   * @param {SVGSVGElement | null} diffToSvg
   */
  updateOutputSvg(headSvg: SVGSVGElement | null, diffFromSvg: SVGSVGElement | null, diffToSvg: SVGSVGElement | null) {
    if (!headSvg) return;

    // Recompute the component SVG as a document fragment.
    let needsSummary = true;
    const outputSymbols = [];
    for (const computed of this.computeOutput(headSvg, diffFromSvg, diffToSvg)) {
      const {symbolId, docId, docFragment} = computed;
      // Serialize to XML.
      const serializer = new XMLSerializer();
      const svgText = serializer.serializeToString(docFragment);
      if (needsSummary) {
        this.outputSvg = svgText;
        needsSummary = false;
      }
      outputSymbols.push({id: symbolId, docId, svgText});
    }
    this.outputSymbols = outputSymbols;
  }

  /**
   * @param {SVGSVGElement} headSvg
   * @param {SVGSVGElement | null} diffFromSvg
   * @param {SVGSVGElement | null} diffToSvg
   * @returns {Array<{symbolId: SymbolId, docId: DocId, docFragment: DocumentFragment}>}
   */
  computeOutput(headSvg: SVGSVGElement, diffFromSvg: SVGSVGElement | null, diffToSvg: SVGSVGElement | null): Array<{symbolId: SymbolId, docId: string, docFragment: DocumentFragment}> {
    const destDoc = headSvg.ownerDocument;

    // Get all symbol IDs.
    const baseSymbolIds = symbolIdsFromRawComponent(this.base);
    const allSymbolIds = new Set(baseSymbolIds);
    const headSymbolIds = symbolIdsFromRawComponent(this.head);
    for (const id of headSymbolIds) {
      allSymbolIds.add(id);
    }

    // Extract each symbol.
    const results = [];
    for (const rawSymbolId of allSymbolIds.values()) {
      const symbolId = new SymbolId(rawSymbolId);

      const docFragment = destDoc.createDocumentFragment();
      const svgEl: SVGSVGElement = destDoc.createElementNS(XMLNS, 'svg');
      const className = headSvg.getAttribute('class');
      if (className) {
        svgEl.setAttribute('class', className);
      }
      // Scale based on the container.
      svgEl.setAttribute('width', '100%');
      let needsViewBox = true;

      const headDocId = this.head ? createDocId(this.head.id, symbolId) : null;
      const baseDocId = this.base ? createDocId(this.base.id, symbolId) : null;
      const docId = headDocId ?? baseDocId ?? '';
      const extracted = headDocId ?
        extractSvgGroup(headSvg, `${headSvg.id}-${headDocId}`) :
        null;
      if (!extracted) {
        // It would not be found if the component were deleted.  Otherwise, we
        // need this.
        if (!this.isRemoved) {
          throw new Error(`Component group not found in SVG: id=${this.id}; sourceEl=${svgEl}, symbolId=${symbolId}`);
        }
      } else {
        const {symbolGroup, style} = extracted;

        if (style) {
          svgEl.append(style);
        }
        svgEl.append(symbolGroup);

        if (diffFromSvg || diffToSvg) {
          svgApplyFilter(svgEl, `${headSvg.id}`);
        }

        // Extract the viewBox from the group element.  We don't use the width and
        // height so that we can scale it based on the container.
        svgEl.setAttribute('viewBox',
          symbolGroup.getAttribute('data-view-box') ?? '');
        needsViewBox = false;
      }

      /**
       * @param {SVGSVGElement} diffSvg
       * @param {Array<string | null>} candidateDocIds Doc ids to try in
       *   order; the first one that resolves to a group is used.  Nulls
       *   (entity not present on that side) are skipped.
       * @param {Array<string>} extraClassNames
       */
      const appendDiffSvg = (
        diffSvg: SVGSVGElement,
        candidateDocIds: Array<string | null>,
        extraClassNames: string[],
      ) => {
        let extracted: ReturnType<typeof extractSvgGroup> = null;
        const tried = new Set<string>();
        for (const candidate of candidateDocIds) {
          if (!candidate || tried.has(candidate)) continue;
          tried.add(candidate);
          extracted = extractSvgGroup(diffSvg, `${diffSvg.id}-${candidate}`);
          if (extracted) break;
        }
        if (!extracted) {
          // Component not found in this diff SVG.
          return;
        }
        const {symbolGroup, style} = extracted;
        if (style) {
          svgEl.append(style);
        }
        svgEl.append(symbolGroup);

        if (extraClassNames.length > 0) {
          symbolGroup.classList.add(...extraClassNames);
        }
        if (needsViewBox) {
          // If we haven't found the view box yet, copy it from the diff SVG.
          // This happens, for example, when a component is deleted.
          const dataViewBox = symbolGroup.getAttribute('data-view-box');
          if (dataViewBox) {
            svgEl.setAttribute('viewBox', dataViewBox);
            needsViewBox = false;
          }
        }
      };

      if (diffFromSvg) {
        // diff_from renders the base file.  Crysknife normally keys its
        // page group by the head id (when alignment hits, e.g. OrCAD)
        // but falls back to the base id when alignment misses
        // (e.g. KiCad rename) — try head first, then base.
        appendDiffSvg(diffFromSvg, [headDocId, baseDocId], ['diff', 'diff_from']);
      }
      if (diffToSvg) {
        // diff_to renders the head file: always keyed by head id.
        appendDiffSvg(diffToSvg, [headDocId], ['diff', 'diff_to']);
      }

      docFragment.append(svgEl);
      results.push({symbolId, docId, docFragment});
    }

    return results;
  }
}

export class LibraryComponentGroup {
  components: Array<LibraryComponent>;
  isShown: boolean;
  /**
   *
   * @param {Array<LibraryComponent>} components
   * @param {boolean} isShown
   */
  constructor(components: Array<LibraryComponent>, isShown: boolean) {
    this.components = components;
    this.isShown = isShown;
  }

  /**
   * Add a component to this group
   */
  addComponent(component: LibraryComponent) {
    this.components.push(component);
  }
}
